import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Lightbulb, Spinner } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Post-challenge review. `review` is the backend-provided list of
 * { question, options, user_answer, correct_answer, is_correct, level, skill }.
 * Each question can expand an AI explanation via /api/challenges/explain.
 */
export default function ChallengeReview({ review = [], lang = 'es' }) {
  const [openIdx, setOpenIdx] = useState(null);
  const [expl, setExpl] = useState({});    // { [idx]: { text, loading } }

  const toggle = async (idx, q) => {
    if (openIdx === idx) { setOpenIdx(null); return; }
    setOpenIdx(idx);
    if (expl[idx]?.text) return;
    setExpl(p => ({ ...p, [idx]: { loading: true } }));
    try {
      const res = await axios.post(`${API}/challenges/explain`, {
        question: q.question,
        user_answer: q.user_answer,
        correct_answer: q.correct_answer,
        options: q.options,
        level: q.level,
        skill: q.skill,
      }, { withCredentials: true });
      setExpl(p => ({ ...p, [idx]: { text: res.data.explanation || '', loading: false } }));
    } catch (e) {
      toast.error(lang === 'es' ? 'Explicación no disponible' : 'Erklärung nicht verfügbar');
      setExpl(p => ({ ...p, [idx]: { text: '', loading: false, error: true } }));
    }
  };

  if (!review?.length) return null;

  return (
    <div className="space-y-3 mt-6" data-testid="challenge-review">
      <h3 className="font-heading text-lg sm:text-xl font-bold text-[#292524]">
        {lang === 'es' ? 'Repaso de preguntas' : 'Fragen-Review'}
      </h3>
      {review.map((q, idx) => {
        const ok = q.is_correct;
        const e = expl[idx];
        return (
          <div
            key={idx}
            className={`rounded-xl border p-4 ${ok ? 'bg-emerald-50/70 border-emerald-200' : 'bg-red-50/70 border-red-200'}`}
            data-testid={`review-q-${idx}`}
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                {ok
                  ? <CheckCircle size={24} weight="fill" className="text-emerald-600" data-testid={`review-tick-ok-${idx}`} />
                  : <XCircle size={24} weight="fill" className="text-red-600" data-testid={`review-tick-ko-${idx}`} />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-[#292524] text-sm sm:text-base">
                  <span className="text-[#78716C] mr-1">#{idx + 1}.</span> {q.question}
                </p>
                <p className="text-xs sm:text-sm mt-1.5">
                  <span className="text-[#78716C]">{lang === 'es' ? 'Tu respuesta:' : 'Deine Antwort:'}</span>{' '}
                  <span className={ok ? 'text-emerald-700 font-semibold' : 'text-red-700 font-semibold'}>
                    {q.user_answer || (lang === 'es' ? '(sin respuesta)' : '(keine Antwort)')}
                  </span>
                </p>
                {!ok && (
                  <p className="text-xs sm:text-sm mt-0.5">
                    <span className="text-[#78716C]">{lang === 'es' ? 'Correcta:' : 'Richtig:'}</span>{' '}
                    <span className="text-emerald-700 font-semibold">{q.correct_answer}</span>
                  </p>
                )}
                <button
                  onClick={() => toggle(idx, q)}
                  className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[#AB1C24] hover:underline"
                  data-testid={`explain-btn-${idx}`}
                >
                  <Lightbulb size={14} weight="fill" />
                  {openIdx === idx
                    ? (lang === 'es' ? 'Ocultar explicación' : 'Erklärung ausblenden')
                    : (lang === 'es' ? 'Ver explicación' : 'Erklärung zeigen')}
                </button>
                {openIdx === idx && (
                  <div className="mt-2 rounded-lg bg-white border border-stone-200 p-3 text-sm text-[#292524] leading-relaxed" data-testid={`explain-body-${idx}`}>
                    {e?.loading && <span className="inline-flex items-center gap-1 text-[#78716C]"><Spinner size={14} className="animate-spin" /> {lang === 'es' ? 'Pensando…' : 'Denke nach…'}</span>}
                    {!e?.loading && e?.text && <p className="whitespace-pre-wrap">{e.text}</p>}
                    {!e?.loading && !e?.text && (
                      <p className="text-[#78716C] italic">{lang === 'es' ? 'No se pudo cargar la explicación.' : 'Erklärung konnte nicht geladen werden.'}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
